import {
  ToolDesignSchema,
  ToolDesignEntrySchema,
  canonicalSha256,
  type CapabilityModel,
  type LlmClient,
  type LlmRequest,
  type Operation,
  type OperationRisk,
  type RiskModel,
  type ToolDesign,
  type ToolDesignEntry,
  type ToolAnnotations,
  type Pagination
} from "@keystone/core";
import {
  actionFromOperationId,
  buildToolName,
  resourceFromPath,
  serviceFromTags,
  snake
} from "./naming.js";
import { buildInputSchema, buildOutputSchema } from "./schema-conversion.js";

export interface DesignOptions {
  serverName: string;
  serverVersion: string;
  /**
   * If true, the designer keeps the literal 1:1 OpenAPI → tool mapping with no
   * annotations / pagination / scopes — this is the naive baseline used in the
   * eval and red-team diffs (ADR-0008).
   */
  naive?: boolean;
  /** Hard cap on tool count (Cursor: 40). Default 80 — we surface a warning above 40. */
  toolBudget?: number;
  /**
   * Optional LLM client. When provided, the designer asks the model to propose
   * workflow clusters (≥2 endpoints folded into one model-facing tool). When
   * omitted, the heuristic 1:1 path runs alone — the existing deterministic
   * fallback. Cached, content-addressed.
   */
  llm?: LlmClient;
  /** Model id to use with `llm`. Default "claude-opus-4-7". */
  llmModel?: string;
  /**
   * Caller-owned cache. When supplied, the designer reads/writes to it; on a hit
   * the cached design is returned directly. Cache key is the content hash of
   * (CapabilityModel, RiskModel, options).
   */
  cache?: Map<string, ToolDesign>;
}

export interface DesignResult {
  design: ToolDesign;
  warnings: string[];
  /** True iff this run produced its output from the cache (no LLM call). */
  cacheHit: boolean;
}

const DESIGNER_VERSION = "designer-v2" as const;

/** Content-addressed cache key for a given (capability, risk, options) tuple. */
function cacheKeyFor(
  capability: CapabilityModel,
  risk: RiskModel,
  options: DesignOptions
): string {
  return canonicalSha256({
    designerVersion: DESIGNER_VERSION,
    capability,
    risk,
    serverName: options.serverName,
    serverVersion: options.serverVersion,
    naive: options.naive === true,
    toolBudget: options.toolBudget ?? null,
    // Only include the model id in the key (NOT the LlmClient instance) so the
    // cache key is purely a function of inputs + designer policy.
    llmModel: options.llm ? (options.llmModel ?? "claude-opus-4-7") : null
  });
}

/**
 * Build a ToolDesign from a CapabilityModel + RiskModel. v0 stays at 1:1 mapping per op;
 * workflow clustering is a future improvement (see CLAUDE.md). The differentiation
 * vs. naive baseline is in the *quality* of each generated tool — naming, descriptions,
 * annotations, schemas, pagination, scopes — not in the count.
 */
export function designServer(
  capability: CapabilityModel,
  risk: RiskModel,
  options: DesignOptions
): DesignResult {
  const warnings: string[] = [];

  // ---- Cache check (Phase 12.1) ----
  const cache = options.cache;
  const cacheKey = cache ? cacheKeyFor(capability, risk, options) : undefined;
  if (cache && cacheKey) {
    const hit = cache.get(cacheKey);
    if (hit) {
      return { design: hit, warnings, cacheHit: true };
    }
  }

  const riskByOp = new Map(risk.operations.map((r) => [r.operationId, r] as const));
  const fallbackService = snake(capability.apiTitle || "api") || "api";

  const seenNames = new Map<string, number>();
  const tools: ToolDesignEntry[] = [];

  for (const op of capability.operations) {
    const r = riskByOp.get(op.id);
    if (!r) {
      warnings.push(`no RiskModel entry for operation "${op.id}" — skipping`);
      continue;
    }
    const name = uniqueName(op, fallbackService, seenNames);
    const tool = options.naive ? buildNaiveTool(op, name) : buildDesignedTool(op, r, name);
    const parsed = ToolDesignEntrySchema.safeParse(tool);
    if (!parsed.success) {
      warnings.push(
        `tool "${name}" failed schema validation (op "${op.id}"): ${parsed.error.issues
          .map((i) => `${i.path.join(".")}: ${i.message}`)
          .join("; ")}`
      );
      continue;
    }
    tools.push(parsed.data);
  }

  const budget = options.toolBudget ?? 80;
  if (tools.length > 40) warnings.push(`tool count ${tools.length} exceeds Cursor's 40-tool limit`);
  if (tools.length > budget) warnings.push(`tool count ${tools.length} exceeds configured budget ${budget}`);

  const design = ToolDesignSchema.parse({
    serverName: options.serverName,
    serverVersion: options.serverVersion,
    tools
  });

  if (cache && cacheKey) cache.set(cacheKey, design);
  return { design, warnings, cacheHit: false };
}

/**
 * LLM-backed designer (Phase 12.1). Layers workflow clustering on top of the
 * heuristic 1:1 design. The model is asked to propose clusters that fold ≥2
 * operations into a single tool with a workflow-y description; the heuristic
 * design is preserved as the fallback if the model proposes nothing.
 *
 * Content-addressed cache: the result is cached by (capability, risk, options)
 * content hash, so a second call with identical inputs is byte-identical and
 * triggers no LLM round-trip.
 */
export async function designServerLlm(
  capability: CapabilityModel,
  risk: RiskModel,
  options: DesignOptions & { llm: LlmClient }
): Promise<DesignResult> {
  // Base heuristic design first (cached).
  const base = designServer(capability, risk, options);
  if (base.cacheHit) return base; // Cache hit on the heuristic path: no LLM call needed.

  const llmModel = options.llmModel ?? "claude-opus-4-7";
  const clusterReq: LlmRequest = {
    model: llmModel,
    messages: [
      {
        role: "system",
        content:
          "You design agent-ergonomic MCP tools. Given the operation list, propose at most 2 workflow clusters that fold ≥2 operations into one tool. Respond as JSON: {clusters: [{name, title, description, operationIds: [...]}, ...]}. If no clustering helps, respond {clusters: []}."
      },
      {
        role: "user",
        content: JSON.stringify({
          apiTitle: capability.apiTitle,
          tools: base.design.tools.map((t) => ({
            name: t.name,
            title: t.title,
            operations: t.operations
          }))
        })
      }
    ],
    temperature: 0,
    maxTokens: 1024
  };
  const resp = await options.llm.complete(clusterReq);
  let clusters: Array<{ name: string; title?: string; description?: string; operationIds: string[] }> = [];
  try {
    const parsed = JSON.parse(resp.text) as { clusters?: typeof clusters };
    clusters = parsed.clusters ?? [];
  } catch {
    clusters = [];
  }

  if (clusters.length === 0) return base;

  // Apply clusters: each cluster folds its operations into one workflow tool.
  // The original per-op tools for those operations are removed; their `requiredScopes`
  // unioned into the workflow tool.
  const opIndex = new Map(base.design.tools.map((t) => [t.operations[0], t] as const));
  const removedOps = new Set<string>();
  const workflowTools: ToolDesignEntry[] = [];
  for (const cluster of clusters) {
    if (!Array.isArray(cluster.operationIds) || cluster.operationIds.length < 2) continue;
    const members = cluster.operationIds.map((id) => opIndex.get(id)).filter((t): t is ToolDesignEntry => !!t);
    if (members.length < 2) continue;

    const scopes = new Set<string>();
    for (const m of members) {
      for (const s of m.requiredScopes) scopes.add(s);
      removedOps.add(m.operations[0]);
    }
    const tool: ToolDesignEntry = {
      name: snake(cluster.name) || `${snake(capability.apiTitle || "api")}_workflow`,
      title: cluster.title ?? `Workflow: ${cluster.name}`,
      description:
        cluster.description ??
        `Workflow tool folding ${members.length} operations: ${members.map((m) => m.name).join(", ")}.`,
      inputSchema: members[0].inputSchema,
      outputSchema: members[0].outputSchema,
      annotations: members[0].annotations,
      operations: cluster.operationIds,
      requiredScopes: [...scopes]
    };
    const parsed = ToolDesignEntrySchema.safeParse(tool);
    if (parsed.success) workflowTools.push(parsed.data);
  }

  if (workflowTools.length === 0) return base;

  const finalTools = base.design.tools.filter((t) => !removedOps.has(t.operations[0])).concat(workflowTools);
  const designed = ToolDesignSchema.parse({
    serverName: options.serverName,
    serverVersion: options.serverVersion,
    tools: finalTools
  });

  if (options.cache) {
    const key = cacheKeyFor(capability, risk, options);
    options.cache.set(key, designed);
  }
  return { design: designed, warnings: base.warnings, cacheHit: false };
}

function uniqueName(op: Operation, fallback: string, seen: Map<string, number>): string {
  const service = serviceFromTags(op, fallback);
  const action = actionFromOperationId(op);
  const resource = resourceFromPath(op.path);
  let name = buildToolName(op, service, action, resource);
  // Cursor's hard cap on tool names: 60 chars. `buildToolName` already trims; here we
  // disambiguate collisions.
  const prev = seen.get(name);
  if (prev !== undefined) {
    seen.set(name, prev + 1);
    name = `${name}_${prev + 1}`;
  } else {
    seen.set(name, 1);
  }
  return name;
}

function buildDesignedTool(op: Operation, r: OperationRisk, name: string): ToolDesignEntry {
  return {
    name,
    title: humanTitle(op),
    description: buildDescription(op, r),
    inputSchema: buildInputSchema(op),
    outputSchema: buildOutputSchema(op),
    annotations: buildAnnotations(op, r),
    pagination: detectPagination(op),
    operations: [op.id],
    requiredScopes: r.suggestedScopes
  };
}

function buildNaiveTool(op: Operation, name: string): ToolDesignEntry {
  // Naive baseline: descriptions are just the summary (or operationId), no pagination,
  // no scope binding, conservative-but-meaningless annotations.
  return {
    name,
    title: op.summary ?? op.id,
    description: (op.summary ?? op.description ?? op.id) + " (naive 1:1 baseline)",
    inputSchema: buildInputSchema(op),
    outputSchema: buildOutputSchema(op),
    // Naive baseline omits accurate annotations. We still satisfy the Zod refinement by
    // claiming "destructiveHint: false" which is exactly the *wrong* default per the MCP
    // spec (destructive defaults to true). This is the bug the eval + red-team will catch.
    annotations: { readOnlyHint: false, destructiveHint: false },
    operations: [op.id],
    requiredScopes: ["api.full"]
  };
}

function humanTitle(op: Operation): string {
  if (op.summary) return op.summary;
  // CamelCase operationId → "Title Case"
  return op.id
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Model-facing description per the mcp-builder method: lead with the verb, name the
 * resource, list inputs and outputs, end with a guardrail clause if destructive.
 */
function buildDescription(op: Operation, r: OperationRisk): string {
  const parts: string[] = [];
  parts.push(op.summary ?? op.description ?? humanTitle(op));
  const params = op.parameters
    .filter((p) => p.location !== "body")
    .map((p) => `${p.name}${p.required ? "" : "?"}`)
    .join(", ");
  if (params) parts.push(`Parameters: ${params}.`);
  if (op.requestBody) parts.push(`Body: JSON object${op.requestBody.required ? " (required)" : ""}.`);
  const outShape = op.responses.find((rr) => rr.status === "200" || rr.status === "201");
  if (outShape) parts.push(`Returns: structured JSON.`);
  if (r.level === "destructive") {
    parts.push(
      `WARNING: this tool is destructive${r.irreversible ? " and irreversible" : ""}. Confirm with the user before calling.`
    );
  }
  if (r.level === "mutate") {
    parts.push(`Note: this tool mutates server state.`);
  }
  // Padding to meet the schema's 20-char minimum.
  const out = parts.join(" ").trim();
  if (out.length < 20) return `${out} ${humanTitle(op)} (${op.method} ${op.path}).`;
  return out;
}

function buildAnnotations(op: Operation, r: OperationRisk): ToolAnnotations {
  const idempotent = op.method === "GET" || op.method === "PUT" || op.method === "DELETE" || op.method === "HEAD";
  return {
    readOnlyHint: r.level === "read",
    destructiveHint: r.level === "destructive",
    idempotentHint: idempotent,
    // openWorldHint: false when proxying to a pinned API (closed world). The MCP spec
    // semantics: `openWorldHint=true` = the tool may interact with arbitrary external
    // entities (e.g., a web search). Our generated tools target a fixed upstream — F-14.
    openWorldHint: false
  };
}

function detectPagination(op: Operation): Pagination | undefined {
  const params = new Map(op.parameters.map((p) => [p.name.toLowerCase(), p]));
  const hasLimit = params.has("limit") || params.has("size") || params.has("per_page");
  if (!hasLimit) return undefined;
  if (params.has("cursor") || params.has("next") || params.has("page_token")) {
    return {
      kind: "cursor",
      paramNames: { cursor: keyFor(params, ["cursor", "next", "page_token"]), limit: keyFor(params, ["limit", "size", "per_page"]) },
      defaultLimit: 20,
      maxLimit: limitMax(params, 100)
    };
  }
  if (params.has("page") || params.has("page_number")) {
    return {
      kind: "page",
      paramNames: { page: keyFor(params, ["page", "page_number"]), limit: keyFor(params, ["limit", "size", "per_page"]) },
      defaultLimit: 20,
      maxLimit: limitMax(params, 100)
    };
  }
  if (params.has("offset") || params.has("skip")) {
    return {
      kind: "offset",
      paramNames: { offset: keyFor(params, ["offset", "skip"]), limit: keyFor(params, ["limit", "size", "per_page"]) },
      defaultLimit: 20,
      maxLimit: limitMax(params, 100)
    };
  }
  return undefined;
}

function keyFor(params: Map<string, unknown>, candidates: string[]): string {
  for (const c of candidates) if (params.has(c)) return c;
  return candidates[0] ?? "";
}

function limitMax(
  params: Map<string, { schema?: unknown }>,
  fallback: number
): number {
  const p = params.get("limit") ?? params.get("size") ?? params.get("per_page");
  const s = p?.schema as Record<string, unknown> | undefined;
  if (s && typeof s.maximum === "number") return s.maximum;
  return fallback;
}
